import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, MessageCircle, ArrowLeft, User, Calendar, Phone } from "lucide-react";

const ABIGAEL_AVATAR = null; // Use initials instead

// ==================== CONVERSATION TREE ====================
const CONVERSATION_TREE = {
  welcome: {
    messages: [
      "Bonjour ! Je suis Abigaël, votre assistante Happyhouse conciergerie.",
      "Comment puis-je vous aider aujourd'hui ?"
    ],
    options: [
      { label: "Je suis propriétaire", next: "proprietaire_menu" },
      { label: "Je suis voyageur", next: "voyageur_menu" },
      { label: "En savoir plus sur Happyhouse conciergerie", next: "about" },
      { label: "Contacter l'équipe", next: "contact_info" },
    ],
  },

  // ---- PROPRIÉTAIRE BRANCH ----
  proprietaire_menu: {
    messages: [
      "Excellent ! En tant que propriétaire, nous avons deux offres pensées pour vous :"
    ],
    options: [
      { label: "Offre Sérénité (loyer garanti)", next: "offre_serenite" },
      { label: "Offre Partenariat (conciergerie)", next: "offre_partenariat" },
      { label: "Simuler mes revenus", next: "simulateur" },
      { label: "Comment ça fonctionne ?", next: "processus_proprio" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  offre_serenite: {
    messages: [
      "L'offre Sérénité, c'est la tranquillité absolue :",
      "Nous devenons votre locataire via un bail civil. Vous recevez un loyer fixe chaque mois, garanti, que votre logement soit occupé ou non.",
      "Nous prenons en charge l'intégralité de la gestion : ménage, accueil voyageurs, maintenance, communication avec les plateformes.",
      "Aucun frais caché, aucune commission. Juste un revenu stable et prévisible."
    ],
    options: [
      { label: "Quels sont les avantages ?", next: "serenite_avantages" },
      { label: "Je suis intéressé(e)", next: "lead_qualification" },
      { label: "Voir l'offre Partenariat", next: "offre_partenariat" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  serenite_avantages: {
    messages: [
      "Les avantages de l'offre Sérénité :",
      "Un loyer garanti chaque mois, sans exception.\nAucune gestion locative de votre côté.\nEntretien professionnel de votre bien.\nProtection juridique via le bail civil.\nValorisation continue de votre patrimoine.",
      "C'est notre offre la plus populaire auprès des propriétaires qui souhaitent un revenu passif et sécurisé."
    ],
    options: [
      { label: "Je suis intéressé(e)", next: "lead_qualification" },
      { label: "Retour aux offres", next: "proprietaire_menu" },
    ],
  },

  offre_partenariat: {
    messages: [
      "L'offre Partenariat, c'est une conciergerie haut de gamme :",
      "Vous restez propriétaire et décidez de vos disponibilités. Nous gérons tout le reste : annonces, réservations, accueil, ménage, états des lieux.",
      "Vous bénéficiez d'une gestion 5 étoiles et d'une optimisation de vos revenus, avec une commission transparente sur les réservations."
    ],
    options: [
      { label: "Quels services sont inclus ?", next: "partenariat_services" },
      { label: "Je suis intéressé(e)", next: "lead_qualification" },
      { label: "Voir l'offre Sérénité", next: "offre_serenite" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  partenariat_services: {
    messages: [
      "Avec l'offre Partenariat, nous prenons en charge :",
      "Création et gestion de vos annonces (Airbnb, Booking, etc.).\nPhotographie professionnelle de votre bien.\nAccueil personnalisé des voyageurs 7j/7.\nMénage inter-séjour avec qualité hôtelière.\nGestion du linge de maison.\nÉtats des lieux photographiques.\nMaintenance et petites réparations.\nOptimisation tarifaire dynamique."
    ],
    options: [
      { label: "Je suis intéressé(e)", next: "lead_qualification" },
      { label: "Retour aux offres", next: "proprietaire_menu" },
    ],
  },

  processus_proprio: {
    messages: [
      "Notre processus est simple et rapide :",
      "1. Échange initial — Nous discutons de votre bien et de vos objectifs.\n2. Visite et estimation — Nous évaluons le potentiel de votre logement.\n3. Choix de l'offre — Sérénité ou Partenariat, selon vos besoins.\n4. Mise en place — Nous préparons et optimisons votre logement.\n5. Gestion complète — Vous n'avez plus qu'à encaisser !",
      "Le tout en quelques jours seulement."
    ],
    options: [
      { label: "Démarrer maintenant", next: "lead_qualification" },
      { label: "Retour aux offres", next: "proprietaire_menu" },
    ],
  },

  simulateur: {
    messages: [
      "Notre simulateur de revenus vous permet d'estimer le potentiel de votre bien en location courte durée.",
      "Rendez-vous sur notre page Propriétaires pour accéder au simulateur et obtenir une estimation personnalisée."
    ],
    options: [
      { label: "Aller au simulateur", next: "link_simulateur" },
      { label: "Être rappelé(e) par un conseiller", next: "lead_qualification" },
      { label: "Retour au menu", next: "welcome" },
    ],
    link: null,
  },

  link_simulateur: {
    messages: ["Je vous redirige vers le simulateur..."],
    redirect: "/proprietaires#simulateur",
    options: [{ label: "Retour au menu", next: "welcome" }],
  },

  // ---- VOYAGEUR BRANCH ----
  voyageur_menu: {
    messages: [
      "Bienvenue ! Nous proposons des logements de qualité à Limoges et Évaux-les-Bains.",
      "Que recherchez-vous ?"
    ],
    options: [
      { label: "Voir les logements à Limoges", next: "logements_limoges" },
      { label: "Voir les logements à Évaux-les-Bains", next: "logements_evaux" },
      { label: "Comment réserver ?", next: "comment_reserver" },
      { label: "Découvrir la région", next: "decouvrir_region" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  logements_limoges: {
    messages: [
      "Nous avons plusieurs logements à Limoges, tous soigneusement décorés et équipés pour votre confort.",
      "Chaque logement inclut : linge de maison, wifi, cuisine équipée et un livret d'accueil avec nos meilleures adresses."
    ],
    redirect: "/voyageurs?ville=Limoges",
    options: [
      { label: "Voir les logements", next: "redirect_voyageurs_limoges" },
      { label: "Comment réserver ?", next: "comment_reserver" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  redirect_voyageurs_limoges: {
    messages: ["Je vous redirige vers nos logements à Limoges..."],
    redirect: "/voyageurs",
    options: [{ label: "Retour au menu", next: "welcome" }],
  },

  logements_evaux: {
    messages: [
      "Évaux-les-Bains est une destination idéale pour une cure thermale ou un séjour de détente.",
      "Nos logements y sont parfaitement situés, à proximité des thermes et du centre-ville."
    ],
    redirect: "/voyageurs?ville=Evaux",
    options: [
      { label: "Voir les logements", next: "redirect_voyageurs_evaux" },
      { label: "Comment réserver ?", next: "comment_reserver" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  redirect_voyageurs_evaux: {
    messages: ["Je vous redirige vers nos logements à Évaux-les-Bains..."],
    redirect: "/voyageurs",
    options: [{ label: "Retour au menu", next: "welcome" }],
  },

  comment_reserver: {
    messages: [
      "Réserver est très simple :",
      "1. Parcourez nos logements sur la page Voyageurs.\n2. Choisissez vos dates et le logement qui vous plaît.\n3. Réservez directement via notre site ou sur Airbnb/Booking.\n4. Nous vous envoyons toutes les informations pour votre arrivée.",
      "Notre équipe est disponible 7j/7 pour vous accueillir personnellement."
    ],
    options: [
      { label: "Voir les logements", next: "redirect_voyageurs_all" },
      { label: "Contacter l'équipe", next: "contact_info" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  redirect_voyageurs_all: {
    messages: ["Je vous redirige vers nos logements..."],
    redirect: "/voyageurs",
    options: [{ label: "Retour au menu", next: "welcome" }],
  },

  decouvrir_region: {
    messages: [
      "La région regorge de trésors à découvrir !",
      "Limoges : capitale de la porcelaine, musées, gastronomie, architecture médiévale.\nÉvaux-les-Bains : thermes romains, patrimoine, nature préservée.",
      "Consultez nos guides complets sur le blog pour préparer votre séjour."
    ],
    options: [
      { label: "Guide Limoges", next: "redirect_guide_limoges" },
      { label: "Guide Évaux-les-Bains", next: "redirect_guide_evaux" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  redirect_guide_limoges: {
    messages: ["Je vous redirige vers le guide Limoges..."],
    redirect: "/blog/limoges-10-incontournables-pour-un-sejour-inoubliable",
    options: [{ label: "Retour au menu", next: "welcome" }],
  },

  redirect_guide_evaux: {
    messages: ["Je vous redirige vers le guide Évaux-les-Bains..."],
    redirect: "/blog/evaux-les-bains-guide-complet-pour-votre-cure-thermale",
    options: [{ label: "Retour au menu", next: "welcome" }],
  },

  // ---- ABOUT ----
  about: {
    messages: [
      "Happyhouse conciergerie est une conciergerie et société de sous-location professionnelle basée à Limoges.",
      "Notre mission : offrir aux propriétaires une gestion sans stress et aux voyageurs un accueil 5 étoiles.",
      "Nous intervenons à Limoges et Évaux-les-Bains, avec une équipe locale, réactive et passionnée."
    ],
    options: [
      { label: "Nos services", next: "nos_services" },
      { label: "Je suis propriétaire", next: "proprietaire_menu" },
      { label: "Je suis voyageur", next: "voyageur_menu" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  nos_services: {
    messages: [
      "Nos services couvrent l'intégralité de la gestion locative :",
      "Remise des clés et accueil personnalisé 7j/7.\nMénage inter-séjour avec qualité hôtelière.\nGestion du linge de maison.\nÉtats des lieux photographiques.\nMaintenance et petites réparations.\nPhotographie professionnelle.\nOptimisation des annonces et des tarifs.\nCommunication avec les voyageurs."
    ],
    options: [
      { label: "Découvrir nos offres", next: "proprietaire_menu" },
      { label: "Contacter l'équipe", next: "contact_info" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  // ---- CONTACT ----
  contact_info: {
    messages: [
      "Vous pouvez nous joindre de plusieurs façons :",
      "Par téléphone : +33 7 67 48 43 13\nPar email : contact@happyhouse-conciergerie.fr\nVia notre formulaire de contact sur le site.",
      "Notre équipe vous répond sous 24h maximum."
    ],
    options: [
      { label: "Aller à la page Contact", next: "redirect_contact" },
      { label: "Être rappelé(e)", next: "lead_qualification" },
      { label: "Retour au menu", next: "welcome" },
    ],
  },

  redirect_contact: {
    messages: ["Je vous redirige vers la page Contact..."],
    redirect: "/contact",
    options: [{ label: "Retour au menu", next: "welcome" }],
  },

  // ---- LEAD QUALIFICATION ----
  lead_qualification: {
    messages: [
      "Avec plaisir ! Pour que notre équipe puisse vous recontacter, j'ai besoin de quelques informations."
    ],
    form: "lead",
    options: [],
  },

  lead_success: {
    messages: [
      "Merci ! Votre demande a bien été enregistrée.",
      "Un conseiller Happyhouse conciergerie vous recontactera dans les plus brefs délais.",
      "En attendant, n'hésitez pas à explorer notre site."
    ],
    options: [
      { label: "Retour au menu", next: "welcome" },
    ],
  },
};

// ==================== CHAT MESSAGE COMPONENT ====================
function ChatMessage({ message, isBot }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex gap-2.5 ${isBot ? "justify-start" : "justify-end"}`}
    >
      {isBot && (
        <div className="w-7 h-7 rounded-full bg-[#004333] flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-[10px] font-bold text-[#C9922A]">A</span>
        </div>
      )}
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-line ${
          isBot
            ? "bg-white text-[#2D2418] shadow-sm border border-[#E0D6CA]/50"
            : "bg-[#004333] text-white"
        }`}
      >
        {message}
      </div>
      {!isBot && (
        <div className="w-7 h-7 rounded-full bg-[#C9922A]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
          <User className="w-3.5 h-3.5 text-[#C9922A]" />
        </div>
      )}
    </motion.div>
  );
}

// ==================== LEAD FORM COMPONENT ====================
function LeadForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.telephone) return;
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className="bg-white rounded-xl border border-[#E0D6CA]/50 shadow-sm p-3.5 space-y-2.5"
      data-testid="chatbot-lead-form"
    >
      <input
        type="text"
        placeholder="Votre nom *"
        value={form.nom}
        onChange={(e) => setForm({ ...form, nom: e.target.value })}
        required
        className="w-full px-3 py-2 text-[13px] rounded-lg border border-[#E0D6CA] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none transition-colors bg-[#FFFDF9]"
        data-testid="chatbot-lead-name"
      />
      <input
        type="email"
        placeholder="Votre email *"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        required
        className="w-full px-3 py-2 text-[13px] rounded-lg border border-[#E0D6CA] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none transition-colors bg-[#FFFDF9]"
        data-testid="chatbot-lead-email"
      />
      <input
        type="tel"
        placeholder="Votre téléphone *"
        value={form.telephone}
        onChange={(e) => setForm({ ...form, telephone: e.target.value })}
        required
        className="w-full px-3 py-2 text-[13px] rounded-lg border border-[#E0D6CA] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none transition-colors bg-[#FFFDF9]"
        data-testid="chatbot-lead-phone"
      />
      <textarea
        placeholder="Votre message (optionnel)"
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        rows={2}
        className="w-full px-3 py-2 text-[13px] rounded-lg border border-[#E0D6CA] focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none transition-colors bg-[#FFFDF9] resize-none"
        data-testid="chatbot-lead-message"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-3 py-2 text-[12px] font-medium text-[#2D2418]/60 bg-[#F5F0EB] rounded-lg hover:bg-[#EDE8E1] transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 px-3 py-2 text-[12px] font-semibold text-white bg-[#004333] rounded-lg hover:bg-[#003328] transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="chatbot-lead-submit"
        >
          {submitting ? (
            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Send className="w-3 h-3" />
              Envoyer
            </>
          )}
        </button>
      </div>
    </motion.form>
  );
}

// ==================== MAIN CHATBOT COMPONENT ====================
export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentNode, setCurrentNode] = useState("welcome");
  const [showForm, setShowForm] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showPulse, setShowPulse] = useState(true);
  const [showTeaser, setShowTeaser] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);
  const teaserTimerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, showForm]);

  // Show welcome messages on first open
  useEffect(() => {
    if (isOpen && !hasGreeted) {
      setHasGreeted(true);
      const node = CONVERSATION_TREE.welcome;
      node.messages.forEach((msg, i) => {
        setTimeout(() => {
          setMessages((prev) => [...prev, { text: msg, isBot: true }]);
        }, i * 400);
      });
    }
  }, [isOpen, hasGreeted]);

  // Hide pulse after some time
  useEffect(() => {
    const timer = setTimeout(() => setShowPulse(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  // Show teaser bubble after delay
  useEffect(() => {
    if (!isOpen && !teaserDismissed) {
      teaserTimerRef.current = setTimeout(() => setShowTeaser(true), 4000);
    }
    return () => clearTimeout(teaserTimerRef.current);
  }, [isOpen, teaserDismissed]);

  // Auto-hide teaser after 12s
  useEffect(() => {
    if (showTeaser) {
      const timer = setTimeout(() => setShowTeaser(false), 12000);
      return () => clearTimeout(timer);
    }
  }, [showTeaser]);

  // Hide teaser when chat opens
  useEffect(() => {
    if (isOpen) {
      setShowTeaser(false);
      setTeaserDismissed(true);
    }
  }, [isOpen]);

  const handleOption = (option) => {
    // Add user message
    setMessages((prev) => [...prev, { text: option.label, isBot: false }]);
    setShowForm(false);

    const node = CONVERSATION_TREE[option.next];
    if (!node) return;

    // Handle redirect
    if (node.redirect) {
      setTimeout(() => {
        node.messages.forEach((msg, i) => {
          setTimeout(() => {
            setMessages((prev) => [...prev, { text: msg, isBot: true }]);
          }, i * 300);
        });
        setTimeout(() => {
          window.location.href = node.redirect;
        }, 800);
      }, 300);
      setCurrentNode(option.next);
      return;
    }

    // Handle form
    if (node.form === "lead") {
      setTimeout(() => {
        node.messages.forEach((msg, i) => {
          setTimeout(() => {
            setMessages((prev) => [...prev, { text: msg, isBot: true }]);
            if (i === node.messages.length - 1) {
              setTimeout(() => setShowForm(true), 300);
            }
          }, i * 300);
        });
      }, 300);
      setCurrentNode(option.next);
      return;
    }

    // Normal node
    setTimeout(() => {
      node.messages.forEach((msg, i) => {
        setTimeout(() => {
          setMessages((prev) => [...prev, { text: msg, isBot: true }]);
        }, i * 350);
      });
    }, 300);
    setCurrentNode(option.next);
  };

  const handleLeadSubmit = async (formData) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nom: formData.nom,
          email: formData.email,
          telephone: formData.telephone,
          message: formData.message,
          type_lead: "chatbot",
        }),
      });
    } catch (err) {
      // Silently fail — still show success
    }

    setShowForm(false);
    setMessages((prev) => [
      ...prev,
      { text: `${formData.nom} — ${formData.telephone}`, isBot: false },
    ]);

    const successNode = CONVERSATION_TREE.lead_success;
    successNode.messages.forEach((msg, i) => {
      setTimeout(() => {
        setMessages((prev) => [...prev, { text: msg, isBot: true }]);
      }, (i + 1) * 350);
    });
    setCurrentNode("lead_success");
  };

  const handleReset = () => {
    setMessages([]);
    setCurrentNode("welcome");
    setHasGreeted(false);
    setShowForm(false);
    setTimeout(() => {
      const node = CONVERSATION_TREE.welcome;
      node.messages.forEach((msg, i) => {
        setTimeout(() => {
          setMessages((prev) => [...prev, { text: msg, isBot: true }]);
        }, i * 400);
      });
      setHasGreeted(true);
    }, 100);
  };

  const currentOptions = CONVERSATION_TREE[currentNode]?.options || [];

  return (
    <>
      {/* Teaser bubble */}
      <AnimatePresence>
        {showTeaser && !isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.95 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="fixed bottom-[7.5rem] right-[5.5rem] z-50 max-w-[220px]"
            data-testid="chatbot-teaser"
          >
            <button
              onClick={() => { setIsOpen(true); setShowTeaser(false); setTeaserDismissed(true); }}
              className="relative bg-white rounded-2xl rounded-br-sm px-4 py-3 shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-[#E0D6CA]/60 text-left group hover:shadow-[0_6px_28px_rgba(0,0,0,0.16)] transition-shadow cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-5 h-5 rounded-full bg-[#004333] flex items-center justify-center flex-shrink-0">
                  <span className="text-[8px] font-bold text-[#C9922A]">A</span>
                </div>
                <span className="text-[11px] font-semibold text-[#004333]">Abigaël</span>
                <span className="relative flex h-2 w-2 ml-auto">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              </div>
              <p className="text-[12px] text-[#6B5E50] leading-snug">
                Bonjour ! Besoin d'un conseil ? Je suis disponible pour vous aider.
              </p>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowTeaser(false); setTeaserDismissed(true); }}
              className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-[#EDE8E1] border border-[#E0D6CA] flex items-center justify-center text-[#8B7D6B] hover:bg-[#E0D6CA] transition-colors shadow-sm"
              data-testid="chatbot-teaser-close"
            >
              <X className="w-3 h-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => { setIsOpen(true); setShowPulse(false); }}
            className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-[#004333] shadow-[0_4px_24px_rgba(0,67,51,0.4)] flex items-center justify-center group"
            data-testid="chatbot-toggle-btn"
          >
            {showPulse && (
              <span className="absolute inset-0 rounded-full bg-[#004333] animate-ping opacity-30" />
            )}
            <MessageCircle className="w-6 h-6 text-[#C9922A] group-hover:scale-110 transition-transform" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={chatRef}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-3rem)] bg-[#F5F0EB] rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.15),0_4px_16px_rgba(0,0,0,0.08)] border border-[#E0D6CA]/60 flex flex-col overflow-hidden"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-[#004333] px-4 py-3.5 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/10 border border-[#C9922A]/40 flex items-center justify-center">
                  <span className="text-sm font-bold text-[#C9922A]">A</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm leading-tight">Abigaël</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-400"></span>
                    </span>
                    <p className="text-green-400/80 text-[11px]">En ligne</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleReset}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                  title="Recommencer"
                  data-testid="chatbot-reset-btn"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                  data-testid="chatbot-close-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin">
              {messages.map((msg, i) => (
                <ChatMessage key={i} message={msg.text} isBot={msg.isBot} />
              ))}

              {/* Lead Form */}
              {showForm && (
                <LeadForm
                  onSubmit={handleLeadSubmit}
                  onCancel={() => {
                    setShowForm(false);
                    setCurrentNode("welcome");
                    setMessages((prev) => [
                      ...prev,
                      { text: "Pas de souci ! N'hésitez pas si vous changez d'avis.", isBot: true },
                    ]);
                  }}
                />
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Options */}
            {!showForm && currentOptions.length > 0 && (
              <div className="border-t border-[#E0D6CA]/50 bg-white/60 backdrop-blur-sm px-3 py-3 flex-shrink-0">
                <div className="flex flex-wrap gap-1.5">
                  {currentOptions.map((opt, i) => (
                    <motion.button
                      key={`${currentNode}-${i}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      onClick={() => handleOption(opt)}
                      className="px-3 py-1.5 text-[12px] font-medium rounded-full border border-[#004333]/20 text-[#004333] bg-white hover:bg-[#004333] hover:text-white hover:border-[#004333] transition-all duration-200 shadow-sm"
                      data-testid={`chatbot-option-${i}`}
                    >
                      {opt.label}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
